import { Fragment, useState } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BellIcon,
  BuildingOfficeIcon,
  QuestionMarkCircleIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import HelpCenterModal from "@components/shared/HelpCenterModal";
import { formatSchemeDay } from "@utils/schemeDay";
import { transformNotification } from "@utils/transformNotification";
import { useAppContext } from "@context/AppContext";
import { getImgSrc } from "@utils/getImgSrc";
import { useAllNotification } from "@hooks/query/useAllNotifications";
import AppNavigation from "./AppNavigation";
import LanguageSwitcher from "@components/shared/LanguageSwitcher";
import UserAvatar from "./UserAvatar";

const menuTransition = {
  as: Fragment,
  enter: "transition ease-out duration-100",
  enterFrom: "transform opacity-0 scale-95",
  enterTo: "transform opacity-100 scale-100",
  leave: "transition ease-in duration-75",
  leaveFrom: "transform opacity-100 scale-100",
  leaveTo: "transform opacity-0 scale-95",
};

const AppBar: React.FunctionComponent = () => {
  const { loggedInInfo } = useAppContext();
  const { t } = useTranslation();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  return (
    <Disclosure
      as="header"
      className="fixed top-0 inset-x-0 z-30 bg-white border-b border-gray-200 shadow-sm"
    >
      {({ open, close }) => (
        <>
          <div className="flex h-14 items-center justify-between gap-4 px-4 sm:px-5">
            <div className="flex min-w-0 items-center gap-3">
              <Disclosure.Button className="-ml-1 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-1 lg:hidden">
                <span className="sr-only">{t("zenith.topnav.open_menu")}</span>
                {open ? (
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                )}
              </Disclosure.Button>

              <Link to="/zenith" className="shrink-0">
                <img
                  className="h-9 w-auto"
                  src={getImgSrc("/zenith-logo.svg")}
                  alt="Zenith"
                />
              </Link>

              {loggedInInfo?.userDetails?.subscriber?.name ? (
                <>
                  <span
                    className="hidden h-6 w-px bg-gray-200 sm:block"
                    aria-hidden="true"
                  />
                  <span className="hidden truncate text-sm text-gray-500 sm:block">
                    {loggedInInfo.userDetails.subscriber.name}
                  </span>
                </>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-4">
              {/* Renders nothing when fewer than two languages are supported. */}
              <LanguageSwitcher />
              <button
                type="button"
                title={t("help_center.title") as string}
                className="rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-1"
                onClick={() => setIsHelpOpen(true)}
              >
                <span className="sr-only">{t("help_center.title")}</span>
                <QuestionMarkCircleIcon
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </button>
              <NotificationsMenu />
              <UserMenu />
            </div>
          </div>

          <Disclosure.Panel
            data-testid="zenith-mobile-nav"
            className="fixed inset-x-0 top-14 bottom-0 overflow-y-auto border-t border-gray-200 bg-white p-4 lg:hidden"
          >
            <AppNavigation isMobile onClose={close} />
          </Disclosure.Panel>

          <HelpCenterModal
            isOpen={isHelpOpen}
            onClose={() => setIsHelpOpen(false)}
          />
        </>
      )}
    </Disclosure>
  );
};

const NotificationsMenu: React.FunctionComponent = () => {
  const { loggedInInfo } = useAppContext();
  const { t } = useTranslation();
  const walletId = loggedInInfo?.userDetails?.walletId;

  const { data: unRead } = useAllNotification({
    params: { recipientWalletId: walletId, onlyNew: 1, limit: -1 },
    enabled: Boolean(walletId),
  });
  // The API returns the newest first.
  const { data: { data: latestNotifications = [] } = {} } = useAllNotification({
    params: { recipientWalletId: walletId, limit: 3 },
    enabled: Boolean(walletId),
  });
  // Undefined until the count loads, so the menu never shows a false "0 unread".
  const unReadCount: number | undefined = unRead?.data?.length;

  const notifications: ReturnType<typeof transformNotification>[] =
    latestNotifications.map((item: any) =>
      transformNotification(item, !item.viewedAt),
    );

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="relative block rounded-full p-1 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-1">
        <span className="sr-only">{t("zenith.topnav.view_notifications")}</span>
        <BellIcon className="h-5 w-5" aria-hidden="true" />
        {unReadCount ? (
          <span className="absolute -top-1 -right-1 grid h-4 min-w-4 place-content-center rounded-full bg-red-500 px-1 text-xxs font-bold text-white ring-2 ring-white">
            {unReadCount > 99 ? "99+" : unReadCount}
          </span>
        ) : null}
      </Menu.Button>
      <Transition {...menuTransition}>
        <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none sm:w-96">
          <div className="flex items-baseline justify-between gap-4 border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-bold text-primary-3">
              {t("zenith.navigation.notifications")}
            </h3>
            <span className="text-xs text-gray-500">
              {unReadCount === undefined
                ? null
                : t("zenith.topnav.unread_messages", { count: unReadCount })}
            </span>
          </div>

          {notifications.map(item => (
            <Menu.Item key={item.id}>
              {({ active }) => (
                <Link
                  to={`/zenith/notifications?id=${item.id}`}
                  data-testid="zenith-bell-notification"
                  className={classNames(
                    "flex gap-3 border-b border-gray-100 px-4 py-3",
                    { "bg-gray-50": active },
                  )}
                >
                  <span
                    className={classNames(
                      "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                      item.isNew ? "bg-red-500" : "bg-gray-200",
                    )}
                    aria-hidden="true"
                  />
                  {item.isNew ? (
                    <span className="sr-only">
                      {t("zenith.topnav.not_read")}
                    </span>
                  ) : null}
                  <div className="min-w-0">
                    <p
                      className={classNames(
                        "line-clamp-2 text-xs text-gray-900",
                        { "font-bold": item.isNew },
                      )}
                    >
                      {item.name}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {formatSchemeDay(item.date)}
                    </p>
                  </div>
                </Link>
              )}
            </Menu.Item>
          ))}

          <Menu.Item>
            {({ active }) => (
              <Link
                to="/zenith/notifications"
                className={classNames(
                  "block rounded-b-lg px-4 py-2.5 text-center text-xs font-semibold text-primary-1",
                  { "bg-gray-50": active },
                )}
              >
                {t("common.view_all")}
              </Link>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

const UserMenu: React.FunctionComponent = () => {
  const { loggedInInfo } = useAppContext();
  const { t } = useTranslation();

  const userNavigation = [
    {
      name: t("zenith.topnav.profile"),
      href: "/zenith/profile/me",
      icon: UserIcon,
    },
    {
      name: t("zenith.topnav.subscriber"),
      href: "/zenith/profile/subscriber",
      icon: BuildingOfficeIcon,
    },
    {
      name: t("zenith.topnav.logout"),
      href: "/zenith/logout",
      icon: ArrowRightOnRectangleIcon,
    },
  ];

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-1 focus-visible:ring-offset-2">
        <span className="sr-only">{t("zenith.topnav.open_user_menu")}</span>
        <UserAvatar name={loggedInInfo?.userDetails?.name} />
      </Menu.Button>
      <Transition {...menuTransition}>
        <Menu.Items className="absolute right-0 mt-2 w-60 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none">
          <div className="border-b border-gray-200 px-4 py-3">
            <p className="text-sm font-bold text-gray-900">
              {loggedInInfo?.userDetails?.name}
            </p>
            <p className="mt-0.5 break-words text-xs text-gray-500">
              {loggedInInfo?.userDetails?.email}
            </p>
          </div>
          <div className="py-1">
            {userNavigation.map(item => (
              <Menu.Item key={item.name}>
                {({ active }) => (
                  <Link
                    to={item.href}
                    className={classNames(
                      "flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-900",
                      { "bg-gray-50": active },
                    )}
                  >
                    <item.icon
                      className="h-4 w-4 text-gray-500"
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default AppBar;
